import React, { useState, useEffect } from "react";
import { Card, Grid, Icon, TextField, Autocomplete } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import {
  getEmployeeEfficiencyReportList,
  exportEmployeeEfficiencyReportData,
} from "actions/reports";
import { employeeEfficiencyReportList } from "constants/reports";
import { useDispatch, useSelector } from "react-redux";
import { getTechnicianList } from "actions/technician";
import { getDepartmentList } from "actions/department";
import { getShiftList } from "actions/shift";
import { getProductList } from "actions/product";
import { getProjectList } from "actions/project";
import { getUnitListing } from "actions/unit";
import { getSubActivityUniqueList } from "actions/subActivity";
import { getMicroActivityUniqueList } from "actions/microActivity";
import { getStationList } from "actions/station";
import {
  autoCompleteProps,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  DATE_RANGE_OPTIONS,
  DEFAULT_RECORDS_PER_PAGE,
  AGGREGATE_OPTIONS,
} from "components/common/constant";
import { useNotify } from "components/CommonComponent/NotificationProvider";

function EmployeeEfficiencyReport() {
  const dispatch = useDispatch();
  const { notify } = useNotify();

  const [pageNum, setPageNum] = useState(1);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [showClearButton, setShowClearButton] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [appliedAggregate, setAppliedAggregate] = useState("");
  const [filters, setFilters] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();

    const startDateString = startDate.toISOString().split("T")[0];
    const endDateString = endDate.toISOString().split("T")[0];

    return {
      dateRange: "TODAY",
      startDate: startDateString,
      endDate: endDateString,

      employeeName: "",
      technicianName: "",
      employeeId: "",
      department: "",
      shift: "",
      product: "",
      projectNo: "",
      unitNo: "",
      station: "",
      subActivity: "",
      microActivity: "",
      aggregate: "",

      filterParams: JSON.stringify([
        {
          field: "Date",
          condition: "gte",
          filterValues: [startDateString],
        },
        {
          field: "Date",
          condition: "lte",
          filterValues: [endDateString],
        },
        // {
        //   field: "Aggregate",
        //   condition: "eq",
        //   filterValues: ["Employee"],
        // },
      ]),
    };
  });

  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );

  const shiftListData = useSelector((state) => state.shift.shiftList);

  const productListData = useSelector((state) => state.product.productList);

  const projectListData = useSelector((state) => state.project.projectList);

  const unitListingData = useSelector((state) => state.unit.unitList);

  const subActivityData = useSelector(
    (state) => state.subActivity.subActivityUniqueList,
  );

  const microActivityData = useSelector(
    (state) => state.microActivity.microActivityUniqueList,
  );

  const stationListData = useSelector((state) => state.station.stationList);

  const reportListData = useSelector(
    (state) => state.employeeActivityReport?.employeeEfficiencyReportList,
  );

  const technicianListData = useSelector(
    (state) => state.technician.technicianList,
  );

  const reportTableData =
    reportListData?.data?.employeeEfficiencyReports ||
    reportListData?.data ||
    [];

  const tableData =
    reportTableData?.length > 0
      ? reportTableData.map((item, index) => ({
          srNo: index + 1,
          date: item.date || "-",
          lhbNo: item.lhbNo || "-",
          employeeName: item.employeeName || "-",
          employeeId: item.employeeId || "-",
          departmentName: item.departmentName || "-",
          shift: item.shift || "-",
          product: item.product || "-",
          projectNo: item.projectNo || "-",
          unitNo: item.unitNo || "-",
          station: item.station || "-",
          subActivity: item.subActivity || "-",
          microActivity: item.microActivity || "-",
          totalHours: item.totalHours || "-",
          optimalHours: item.optimalHours || "-",
          efficiencyPercentage: item.efficiencyPercentage || "-",
        }))
      : [];

  useEffect(() => {
    if (reportListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords:
          (reportListData?.data?.employeeEfficiencyReports?.length === 0 &&
            reportListData?.data?.employeeEfficiencyReports !== undefined) ||
          (Array.isArray(reportListData?.data) &&
            reportListData?.data.length === 0),
        showLoaderOnClick: false,
      });
    } else if (reportListData?.code || reportListData?.message) {
      setListState({
        isLoading: false,
        error:
          reportListData?.message ||
          "Failed to load employee efficiency report!",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [reportListData]);

  useEffect(() => {
    getEmployeeEfficiencyReportList(dispatch, pageNum, filters.filterParams);

    return () => {
      dispatch({
        type: employeeEfficiencyReportList,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    getDepartmentList(dispatch);
    getTechnicianList(dispatch);
    getShiftList(dispatch);
    getProductList(dispatch);
    getProjectList(dispatch);
    getUnitListing(dispatch);
    getSubActivityUniqueList(dispatch);
    getMicroActivityUniqueList(dispatch);
    getStationList(dispatch);
  }, [dispatch]);

  const getDateRange = (days) => {
    const today = new Date();

    if (days === "TODAY") {
      const todayString = dayjs(today).format("YYYY-MM-DD");

      return {
        startDate: todayString,
        endDate: todayString,
      };
    }

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 1);

    if (days === "1") {
      const yesterdayString = dayjs(endDate).format("YYYY-MM-DD");

      return {
        startDate: yesterdayString,
        endDate: yesterdayString,
      };
    }

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (Number(days) - 1));

    return {
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
    };
  };

  const columns =
    appliedAggregate !== ""
      ? [
          { Header: "Sr No", accessor: "srNo", width: "115px" },
          {
            Header: "Employee Name",
            accessor: "employeeName",
            width: "190px",
            Cell: (cell) => wrapCell(cell.value, "190px"),
          },
          {
            Header: "Employee ID",
            accessor: "employeeId",
            width: "170px",
            Cell: (cell) => wrapCell(cell.value, "150px"),
          },
          {
            Header: "Department",
            accessor: "departmentName",
            width: "180px",
            Cell: (cell) => wrapCell(cell.value, "180px"),
          },
          {
            Header: "Total Hours",
            accessor: "totalHours",
            width: "120px",
            Cell: (cell) => wrapCell(cell.value, "120px"),
          },
          {
            Header: "Optimal Hours",
            accessor: "optimalHours",
            width: "120px",
            Cell: (cell) => wrapCell(cell.value, "120px"),
          },
          {
            Header: "Efficiency %",
            accessor: "efficiencyPercentage",
            width: "130px",
            Cell: (cell) => wrapCell(cell.value, "130px"),
          },
        ]
      : [
          { Header: "Sr No", accessor: "srNo", width: "115px" },
          {
            Header: "Date",
            accessor: "date",
            width: "110px",
            Cell: (cell) => wrapCell(cell.value, "110px"),
          },
          {
            Header: "LHB No",
            accessor: "lhbNo",
            width: "125px",
            Cell: (cell) => wrapCell(cell.value, "110px"),
          },
          {
            Header: "Employee Name",
            accessor: "employeeName",
            width: "190px",
            Cell: (cell) => wrapCell(cell.value, "190px"),
          },
          {
            Header: "Employee ID",
            accessor: "employeeId",
            width: "170px",
            Cell: (cell) => wrapCell(cell.value, "150px"),
          },
          {
            Header: "Department",
            accessor: "departmentName",
            width: "180px",
            Cell: (cell) => wrapCell(cell.value, "180px"),
          },
          {
            Header: "Shift",
            accessor: "shift",
            width: "140px",
            Cell: (cell) => wrapCell(cell.value, "140px"),
          },
          {
            Header: "Product",
            accessor: "product",
            width: "140px",
            Cell: (cell) => wrapCell(cell.value, "140px"),
          },
          {
            Header: "Project No",
            accessor: "projectNo",
            width: "160px",
            Cell: (cell) => wrapCell(cell.value, "160px"),
          },
          {
            Header: "Unit No",
            accessor: "unitNo",
            width: "130px",
            Cell: (cell) => wrapCell(cell.value, "130px"),
          },
          {
            Header: "Station",
            accessor: "station",
            width: "150px",
            Cell: (cell) => wrapCell(cell.value, "150px"),
          },
          {
            Header: "Sub Activity",
            accessor: "subActivity",
            width: "250px",
            Cell: (cell) => wrapCell(cell.value, "250px"),
          },
          {
            Header: "Micro Activity",
            accessor: "microActivity",
            width: "280px",
            Cell: (cell) => wrapCell(cell.value, "280px"),
          },
          {
            Header: "Total Hours",
            accessor: "totalHours",
            width: "120px",
            Cell: (cell) => wrapCell(cell.value, "120px"),
          },
          {
            Header: "Optimal Hours",
            accessor: "optimalHours",
            width: "120px",
            Cell: (cell) => wrapCell(cell.value, "120px"),
          },
          {
            Header: "Efficiency %",
            accessor: "efficiencyPercentage",
            width: "170px",
            Cell: (cell) => wrapCell(cell.value, "130px"),
          },
        ];

  const shiftOptions =
    shiftListData?.data?.shift?.map((s) => ({ id: s.ID, name: s.name })) || [];

  const products = productListData?.data?.Product || [];

  const projectOptions =
    projectListData?.data?.project?.map((item) => ({
      id: item.ID,
      projectNo: item.projectNo,
    })) || [];

  const units = unitListingData?.data?.Unit || [];

  const subActivityOptions =
    subActivityData?.data?.map((item) => ({ id: item.ID, name: item.name })) ||
    [];

  const microActivityOptions =
    microActivityData?.data?.microActivities?.map((item) => ({
      id: item.ID,
      name: item.name,
    })) || [];

  const stationOptions = stationListData?.data?.station || [];

  const buildFilterParams = () => {
    const filterParams = [];

    filterParams.push({
      field: "Date",
      condition: "gte",
      filterValues: [filters.startDate],
    });

    filterParams.push({
      field: "Date",
      condition: "lte",
      filterValues: [filters.endDate],
    });

    if (filters.employeeName?.trim()) {
      filterParams.push({
        field: "EmployeeName",
        condition: "like",
        filterValues: [filters.employeeName.trim()],
      });
    }

    if (filters.employeeId?.trim()) {
      filterParams.push({
        field: "EmployeeID",
        condition: "like",
        filterValues: [filters.employeeId.trim()],
      });
    }

    if (filters.department) {
      const selectedDepartment = departmentListData?.data?.Department?.find(
        (item) => item.name === filters.department,
      );

      if (selectedDepartment) {
        filterParams.push({
          field: "DepartmentName",
          condition: "like",
          filterValues: [selectedDepartment.name],
        });
      }
    }

    if (filters.shift?.trim()) {
      filterParams.push({
        field: "Shift",
        condition: "like",
        filterValues: [filters.shift.trim()],
      });
    }

    if (filters.product?.trim()) {
      filterParams.push({
        field: "Product",
        condition: "like",
        filterValues: [filters.product.trim()],
      });
    }

    if (filters.projectNo?.trim()) {
      filterParams.push({
        field: "ProjectNo",
        condition: "like",
        filterValues: [filters.projectNo.trim()],
      });
    }

    if (filters.unitNo?.trim()) {
      filterParams.push({
        field: "UnitNo",
        condition: "like",
        filterValues: [filters.unitNo.trim()],
      });
    }

    if (filters.station?.trim()) {
      filterParams.push({
        field: "Station",
        condition: "like",
        filterValues: [filters.station.trim()],
      });
    }

    if (filters.subActivity?.trim()) {
      filterParams.push({
        field: "SubActivity",
        condition: "like",
        filterValues: [filters.subActivity.trim()],
      });
    }

    if (filters.microActivity?.trim()) {
      filterParams.push({
        field: "MicroActivity",
        condition: "like",
        filterValues: [filters.microActivity.trim()],
      });
    }

    if (filters.aggregate) {
      filterParams.push({
        field: "Aggregate",
        condition: "eq",
        filterValues: [filters.aggregate],
      });
    }

    if (filters.technicianName) {
      const technician = technicianListData?.data?.Technicians?.find(
        (item) => item.id === filters.technicianName,
      );

      if (technician) {
        filterParams.push({
          field: "EmployeeName",
          condition: "like",
          filterValues: [technician.name],
        });
      }
    }

    return JSON.stringify(filterParams);
  };

  const handleSearch = () => {
    const filterParams = buildFilterParams();

    if (filterParams === filters.filterParams) {
      return;
    }

    setAppliedAggregate(filters.aggregate);
    setListState((prev) => ({
      ...prev,
      isLoading: true,
      showLoaderOnClick: true,
    }));

    setFilters((prev) => ({
      ...prev,
      filterParams,
    }));

    setShowClearButton(true);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleClear = () => {
    const today = dayjs().format("YYYY-MM-DD");

    setAppliedAggregate("");

    setFilters({
      dateRange: "TODAY",
      startDate: today,
      endDate: today,
      aggregate: "",
      employeeName: "",
      technicianName: "",
      employeeId: "",
      department: "",
      shift: "",
      product: "",
      projectNo: "",
      unitNo: "",
      station: "",
      subActivity: "",
      microActivity: "",
      filterParams: JSON.stringify([
        {
          field: "Date",
          condition: "gte",
          filterValues: [today],
        },
        {
          field: "Date",
          condition: "lte",
          filterValues: [today],
        },
      ]),
    });

    setShowClearButton(false);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const downloadEmployeeEfficiencyReportCSV = async () => {
    try {
      setExportLoading(true);

      const res = await exportEmployeeEfficiencyReportData(filters.filterParams);
      const url = window.URL.createObjectURL(res.data);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "EmployeeEfficiencyReport.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      notify(
        res?.message ||
          "Employee Efficiency Report file downloaded successfully",
        "success",
      );
    } catch (error) {
      notify("Failed to download employee efficiency report file", "error");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Employee Efficiency Report
                </MDTypography>

                <MDBox display="flex" gap={2}>
                  <MDButton
                    variant="gradient"
                    color="light"
                    startIcon={<Icon>file_upload</Icon>}
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      textTransform: "none",
                    }}
                    onClick={downloadEmployeeEfficiencyReportCSV}
                  >
                    {exportLoading ? (
                      <>
                        <i className="fa fa-spinner fa-spin mr-2" />
                        Exporting...
                      </>
                    ) : (
                      "Export Report"
                    )}
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox px={3} pb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={DATE_RANGE_OPTIONS}
                      value={
                        DATE_RANGE_OPTIONS.find(
                          (item) => item.value === filters.dateRange,
                        ) || null
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(event, value) => {
                        if (!value) return;

                        if (value.value === "CUSTOM") {
                          setFilters((prev) => ({
                            ...prev,
                            dateRange: "CUSTOM",
                          }));
                        } else {
                          const dates = getDateRange(value.value);

                          setFilters((prev) => ({
                            ...prev,
                            dateRange: value.value,
                            startDate: dates.startDate,
                            endDate: dates.endDate,
                          }));
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Date Range"
                        />
                      )}
                    />
                  </Grid>

                  {filters.dateRange === "CUSTOM" && (
                    <>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="Start Date"
                            value={dayjs(filters.startDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                startDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="End Date"
                            minDate={dayjs(filters.startDate)}
                            value={dayjs(filters.endDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                endDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={technicianListData?.data?.Technicians || []}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        technicianListData?.data?.Technicians?.find(
                          (item) => item.id === filters.technicianName,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          technicianName: newValue?.id || "",
                          technicianId: newValue?.id || "",
                        }))
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Technician"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={AGGREGATE_OPTIONS}
                      getOptionLabel={(option) => option?.label || ""}
                      value={
                        AGGREGATE_OPTIONS.find(
                          (item) => item.value === filters.aggregate,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          aggregate: newValue?.value || "",
                        }))
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Aggregate" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Employee Name"
                      value={filters.employeeName}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          employeeName: e.target.value,
                        }))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Employee ID"
                      value={filters.employeeId}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          employeeId: e.target.value,
                        }))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={departmentListData?.data?.Department || []}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        departmentListData?.data?.Department?.find(
                          (item) => item.name === filters.department,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          department: newValue?.name || "",
                        }))
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.name === value?.name
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Department"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={shiftOptions}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        shiftOptions.find((s) => s.name === filters.shift) ||
                        null
                      }
                      onChange={(event, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          shift: newValue?.name || "",
                        }))
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Shift" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={products}
                      getOptionLabel={(option) => option.type || ""}
                      value={
                        products.find((p) => p.type === filters.product) || null
                      }
                      onChange={(e, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          product: newValue?.type || "",
                        }))
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Product" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={projectOptions}
                      getOptionLabel={(option) => option.projectNo || ""}
                      value={
                        projectOptions.find(
                          (p) => p.projectNo === filters.projectNo,
                        ) || null
                      }
                      onChange={(e, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          projectNo: newValue?.projectNo || "",
                        }))
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Project No"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={units.filter((u) => u.status === "ACTIVE")}
                      getOptionLabel={(option) => option.unitNo || ""}
                      value={
                        units.find((u) => u.unitNo === filters.unitNo) || null
                      }
                      onChange={(e, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          unitNo: newValue?.unitNo || "",
                        }))
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Unit" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={stationOptions}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        stationOptions.find(
                          (s) => s.name === filters.station,
                        ) || null
                      }
                      onChange={(e, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          station: newValue?.name || "",
                        }))
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select station" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={subActivityOptions}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        subActivityOptions.find(
                          (s) => s.name === filters.subActivity,
                        ) || null
                      }
                      onChange={(e, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          subActivity: newValue?.name || "",
                        }))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Sub Activity"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={microActivityOptions}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        microActivityOptions.find(
                          (m) => m.name === filters.microActivity,
                        ) || null
                      }
                      onChange={(e, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          microActivity: newValue?.name || "",
                        }))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Micro Activity"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg="auto">
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {showClearButton && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox px={3} py={1}>
                <MDTypography variant="body1" color="deepBlue">
                  Total Records: {reportListData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="error">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="text">
                      No records found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={tableData}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (reportListData?.data?.totalRecords || 0) /
                        (reportListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default EmployeeEfficiencyReport;
